import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';
import DropdownSelect, { components } from 'react-select';
import Icon from '@jll/icon';
import styles from './select.scss';
import {
    container,
    control,
    menu,
    placeholder,
    option,
    singleValue,
    valueContainer,
    menuList,
    multiValue,
    multiValueLabel,
    multiValueRemove,
    dropdownIndicator,
    noOptionsMessage,
    input
} from './select-variables';

const handleTextHighlight = (item, searchValue) => {
    const matchStartsAt = item.toLowerCase().indexOf(searchValue.toLowerCase());
    const matchEndsAt = matchStartsAt + searchValue.length - 1;

    const textBeforeMatched = item.slice(0, matchStartsAt);
    const matchedText = item.slice(matchStartsAt, matchEndsAt + 1);
    const textAfterMatched = item.slice(matchEndsAt + 1);

    return (
        <Fragment>
            {matchedText && matchStartsAt !== -1 ? (
                <Fragment>
                    {textBeforeMatched}
                    <strong className={styles['select--matched']}>{matchedText}</strong>
                    {textAfterMatched}
                </Fragment>
            ) : (
                <Fragment>{item}</Fragment>
            )}
        </Fragment>
    );
};

const DropdownIndicator = props => {
    const getStyles = classNames.bind(styles);
    const searchClass = getStyles(styles['select__search'], 'far fa-search');
    const arrowClass = getStyles(styles['select__arrow'], 'fas fa-chevron-down');

    // eslint-disable-next-line
    if (props.selectProps.isSearchable) {
        return (
            <components.DropdownIndicator {...props}>
                <Icon className={searchClass} />
            </components.DropdownIndicator>
        );
    }
    return (
        <components.DropdownIndicator {...props}>
            <Icon className={arrowClass} />
        </components.DropdownIndicator>
    );
};

const MultiValueRemove = props => {
    const getStyles = classNames.bind(styles);
    const closeClass = getStyles(styles['select__multi-close'], 'far fa-times');

    return (
        <components.MultiValueRemove {...props}>
            <Icon className={closeClass} />
        </components.MultiValueRemove>
    );
};

const Option = props => {
    const getStyles = classNames.bind(styles);
    const selectedClass = getStyles(styles['select--selected'], 'fas fa-check');

    return (
        <components.Option {...props}>
            {props.selectProps.isSearchable ? handleTextHighlight(props.data.label, props.selectProps.inputValue) : props.data.label}
            {props.isSelected ? (
                <Icon className={selectedClass} />
            ) : (
                <span className={styles['select--abbr']}>
                    {props.data.abbr && (props.selectProps.isSearchable ? handleTextHighlight(props.data.abbr, props.selectProps.inputValue) : props.data.abbr)}
                </span>
            )}
        </components.Option>
    );
};

const Select = props => {
    const {
        className,
        inputId,
        label,
        name,
        options,
        noOptionsText,
        isMulti,
        isSearchable,
        hasError,
        helpText,
        isRequired,
        hasInsetLabel,
        insetLabel,
        wrapperProperties,
        ...other
    } = props;

    const getStyles = classNames.bind(styles);
    const selectClasses = getStyles('select', className);
    const helpTextClasses = getStyles('select__help-message', {
        'select__help-message--error': hasError
    });
    const requiredClass = getStyles(styles['select__label--required'], 'far fa-asterisk');

    const wrapperProps = {
        className: selectClasses,
        ...wrapperProperties
    };

    return (
        <div {...wrapperProps}>
            {label !== '' && (
                <label htmlFor={inputId || name} className={styles['select__label']}>
                    {label}
                    {isRequired ? <Icon className={requiredClass} /> : null}
                </label>
            )}
            <DropdownSelect
                components={{ DropdownIndicator, MultiValueRemove, Option }}
                arrowRenderer={false}
                inputId={inputId}
                options={options}
                classNamePrefix="select"
                noOptionsMessage={() => noOptionsText}
                isClearable={false}
                isMulti={!hasInsetLabel ? isMulti : false}
                isSearchable={isSearchable}
                hasError
                name={name}
                required={isRequired}
                styles={{
                    container: (base, state) => container(base, state),
                    control: (base, state) => control(base, state, hasError),
                    placeholder: (base, state) => placeholder(base, state, insetLabel, hasInsetLabel),
                    singleValue: (base, state) => singleValue(base, state, insetLabel, hasInsetLabel),
                    valueContainer: (base, state) => valueContainer(base, state),
                    indicatorSeparator: () => {},
                    input: base => input(base),
                    menuList: base => menuList(base),
                    menu: base => menu(base),
                    option: (base, state) => option(base, state),
                    multiValue: (base, state) => multiValue(base, state),
                    multiValueLabel: (base, state) => multiValueLabel(base, state),
                    multiValueRemove: (base, state) => multiValueRemove(base, state),
                    dropdownIndicator: (base, state) => dropdownIndicator(base, state),
                    noOptionsMessage: base => noOptionsMessage(base)
                }}
                {...other}
            />
            {helpText && (
                <p id={`${inputId}-help-message`} className={helpTextClasses}>
                    {helpText}
                </p>
            )}
        </div>
    );
};

Select.propTypes = {
    /** The classes of the wrapper. */
    className: PropTypes.string,
    /** Label inside input field. It is only visible, when hasInsetLabel set to true */
    insetLabel: PropTypes.string,
    /** If set to true, adjust styling to show error state. Error message is set through helpText prop. */
    hasError: PropTypes.bool,
    /** Additional information about the component to help user. */
    helpText: PropTypes.string,
    /** Select component input ID. RECOMMENDED to always declare an unique ID, this prop is used for accessibility purpose also. */
    inputId: PropTypes.string,
    /** If set to true, adjusts styling properly and disables Multi selection */
    hasInsetLabel: PropTypes.bool,
    /** Allows user to select multiple options. Multi selection is not working, when hasInsetLabel set to true */
    isMulti: PropTypes.bool,
    /** Allows user to search in options. */
    isSearchable: PropTypes.bool,
    /** The title of the input field. */
    label: PropTypes.string,
    /** Unique name of the select input. This prop is used to make input unique and for accessibility purpose. */
    name: PropTypes.string,
    /** Informational message, which appears when there are no more possible selections. */
    noOptionsText: PropTypes.string,
    /** Array of possible select options. fields of each option are (label, value, abbr(optional)) */
    options: PropTypes.arrayOf(PropTypes.object),
    /** If set to true, makes input required */
    isRequired: PropTypes.bool
};

Select.defaultProps = {
    className: '',
    insetLabel: '',
    hasError: false,
    helpText: '',
    inputId: '',
    hasInsetLabel: false,
    isMulti: false,
    isSearchable: false,
    label: '',
    name: '',
    noOptionsText: '',
    options: [],
    isRequired: false
};

export default Select;
